import Backbone from 'backbone';

import { CreateUserType } from '@biteinc/enums';
import { StringHelper } from '@biteinc/helpers';

import { convertCsvLinesToCsvData } from '../csv_builder';
import { parseUserImportCsv } from '../user_import_csv_parser';

const CsvTemplateHeaders = [
  'Org Id',
  'Email Address',
  'First Name',
  'Last Name',
  'Role',
  'Location ID or Location Group ID',
  'Type',
  'Access Code',
  'KOR Subscription List',
  'KOR Delivery',
  'KOR Digest Time',
  'KOR Readiness Threshold Percent',
];

app.UserImportModal = Backbone.View.extend({
  className: 'user-import-modal',

  setSuperView(superView) {
    this.superView = superView;
  },

  clearState() {
    this.superView = null;
    this._selectedFile = null;
  },

  _downloadCsvTemplate() {
    const aLink = document.createElement('a');
    aLink.download = 'user-import-template.csv';
    aLink.href = convertCsvLinesToCsvData([CsvTemplateHeaders]);
    aLink.dispatchEvent(new MouseEvent('click'));
  },

  _runImport() {
    const reader = new FileReader();
    reader.onload = () => {
      const { errorMessages, userRows } = parseUserImportCsv(reader.result);
      if (errorMessages.length) {
        const errorListHtml = errorMessages
          .map((errorMessage) => `<li>${StringHelper.escapeHtml(errorMessage)}</li>`)
          .join('');
        new app.AlertView().show('Cannot import this CSV file', `<ol>${errorListHtml}</ol>`);
        return;
      }

      const inStoreUserCount = userRows.filter((userRow) => {
        return userRow.type === CreateUserType.InStore;
      }).length;
      const bureauUserCount = userRows.length - inStoreUserCount;
      const orgCount = new Set(userRows.map((userRow) => userRow.orgId)).size;
      const confirmed = window.confirm(
        `Import ${userRows.length} users into ${orgCount} org${orgCount === 1 ? '' : 's'} ` +
          `(${bureauUserCount} Bite Admin Portal, ${inStoreUserCount} In-Store)?`,
      );
      if (!confirmed) {
        return;
      }

      const $importButton = this._$importButton;
      const reqId = $importButton.initLoadingButton(
        $importButton.html(),
        'Importing...',
        'Imported!',
      );
      app.makeRequestWithOptions({
        method: 'POST',
        url: '/api/v2/users/import',
        data: { users: userRows },
        onSuccess: (data) => {
          $importButton.loadingDidFinishSuccessfully(reqId);
          this.trigger(app.ModalView.Events.ModalViewShouldHide, this);
          const summaryListHtml = (data.summaryDescriptions || [])
            .map((description) => `<li>${StringHelper.escapeHtml(description)}</li>`)
            .join('');
          new app.AlertView().show(
            `Imported ${userRows.length} users into ${orgCount} org${orgCount === 1 ? '' : 's'}`,
            `<ul>${summaryListHtml}</ul>`,
          );
        },
        onFailure: () => {
          $importButton.loadingDidFinishWithError(reqId);
        },
      });
    };
    reader.readAsText(this._selectedFile);
  },

  render() {
    this.superView.$titleLabel.text('Import Users from CSV');
    this.superView.footer.$el.hide();

    const $step1 = $(
      // prettier-ignore
      '<div class="mb-4">' +
        '<div class="mb-2">' +
          '<strong>Step 1.</strong> Download the blank CSV template and fill in one row per user.' +
        '</div>' +
      '</div>',
    );
    const $downloadButton = $(
      '<button type="button" class="btn btn-sm btn-primary">Download Blank CSV</button>',
    );
    $downloadButton.click(() => {
      this._downloadCsvTemplate();
    });
    $step1.append($downloadButton);

    const $step2 = $(
      // prettier-ignore
      '<div class="mb-4">' +
        '<div class="mb-2">' +
          '<strong>Step 2.</strong> Upload the filled-in file.' +
          '<ul class="mt-1 mb-2 text-muted small">' +
            '<li><strong>Org Id</strong>: The Bite ID of the org the user will be a part of.</li>' +
            '<li><strong>Email Address</strong>: Becomes the username; the registration email is sent here.</li>' +
            '<li><strong>First Name / Last Name</strong>: Optional; never overwrite an existing user\'s name.</li>' +
            '<li><strong>Role</strong>: The name of one of the org\'s roles, e.g. "Store Manager".</li>' +
            '<li><strong>Location ID or Location Group ID</strong>: The Bite ID of the location or location group the user can access.</li>' +
            '<li><strong>Type</strong>: "Bite Admin Portal" (default) or "In-Store".</li>' +
            '<li><strong>Access Code</strong>: A 4-digit code; required for In-Store users, not allowed for Bite Admin Portal users.</li>' +
            '<li><strong>KOR Subscription List</strong>: Optional; "All" subscribes the user to every KOR alert for the location access above, or list specific alert keys separated by commas (e.g. "failure:offline, failure:printer"). Blank or "No" skips it.</li>' +
            '<li><strong>KOR Delivery</strong>: Optional unless a KOR Subscription List is set; "Live", "Digest", or "Both".</li>' +
            '<li><strong>KOR Digest Time</strong>: Optional unless a KOR Subscription List is set; the time of day for digest delivery, e.g. "8:00" or "6pm". Only takes effect when KOR Delivery is "Digest" or "Both".</li>' +
            '<li><strong>KOR Readiness Threshold Percent</strong>: Optional; a whole number from 0 to 100 to alert when the location access\'s KOR drops below it. Blank or "Off" leaves it off.</li>' +
          '</ul>' +
        '</div>' +
      '</div>',
    );
    const $fileInput = $('<input type="file" accept=".csv,text/csv" class="d-none">');
    const $chooseFileButton = $(
      '<button type="button" class="btn btn-sm btn-primary">Choose File</button>',
    );
    const $fileNameLabel = $('<span class="ms-2 text-muted">No file selected</span>');
    $chooseFileButton.click(() => {
      $fileInput.trigger('click');
    });
    $fileInput.change(() => {
      const file = $fileInput[0].files[0];
      // Clear the input so that re-selecting the same file fires this handler again.
      $fileInput.val('');
      if (!file) {
        return;
      }
      this._selectedFile = file;
      $fileNameLabel.text(file.name);
      this._$importButton.prop('disabled', false);
    });
    $step2.append($fileInput, $chooseFileButton, $fileNameLabel);

    const $step3 = $(
      // prettier-ignore
      '<div class="mb-2">' +
        '<div class="mb-2">' +
          '<strong>Step 3.</strong> Run the import. Nothing is imported unless every row is valid.' +
        '</div>' +
      '</div>',
    );
    this._$importButton = $(
      '<button type="button" class="btn btn-sm btn-primary" disabled>Import Users</button>',
    );
    this._$importButton.click(() => {
      this._runImport();
    });
    $step3.append(this._$importButton);

    this.$el.append($step1, $step2, $step3);
    return this;
  },
});
